/**
 * Finding a quoted passage in the document it came from.
 *
 * Every check on the finishing screen reports a QUOTE and expects the editor to
 * go there. The text checks quote a sentence verbatim, so a plain `indexOf` was
 * enough - and the legal review does not, which is why 該当箇所へ移動 on a legal
 * finding did nothing at all. Its quotes come from `excerptAround`
 * (server/modules/compliance/rules.ts): twenty characters either side of the
 * match, with `…` glued on wherever the excerpt was cut, and every run of
 * whitespace collapsed to one space. None of those three transformations
 * survives a substring search against the document.
 *
 * There was no error either. `jumpToText` returned silently when it found
 * nothing, so pressing the button was indistinguishable from a dead control -
 * which is exactly what it was for the panel that needs it most.
 *
 * So the search is done on a NORMALISED copy with an index back to the real
 * positions: whitespace collapsed the same way the excerpt collapses it, and the
 * ellipses trimmed off the ends before looking. Its own module because the
 * mapping is the fiddly part and it is worth a test that needs no browser.
 */

/** One character of the normalised text, and where it really lives. */
interface Mapped {
  char: string;
  /** Position in the source string this character came from. */
  at: number;
}

/**
 * Collapse whitespace, remembering where everything was.
 *
 * A run of spaces or newlines becomes ONE space, mapped to the first character
 * of the run - so a match that starts at a collapsed space still resolves to a
 * real position, and a match's end is computed from the last character it
 * actually covered rather than by adding the needle's length.
 */
function normalise(text: string): Mapped[] {
  const out: Mapped[] = [];
  let inSpace = false;
  for (let at = 0; at < text.length; at += 1) {
    const char = text[at]!;
    if (/\s/.test(char)) {
      if (!inSpace) {
        out.push({ char: ' ', at });
        inSpace = true;
      }
      continue;
    }
    inSpace = false;
    out.push({ char, at });
  }
  return out;
}

/**
 * The quote, ready to be looked for.
 *
 * The ellipses are the excerpt's own punctuation - they mean "this was cut" and
 * appear nowhere in the article - so they are trimmed rather than searched for.
 * A quote that is nothing BUT ellipses is not a quote.
 */
export function normaliseQuote(quote: string): string {
  return (
    quote
      /*
       * The quote comes from the stored MARKDOWN and is looked for in the
       * DOCUMENT, and the two do not spell the same article the same way. A
       * finding on the first sentence of a section arrives as
       * 「## はじめに この施術は…」; the editor has a heading node whose text is
       * 「はじめに」 and no hashes anywhere. Same for a bolded price - the quote
       * carries the asterisks and the document carries bold text.
       *
       * Dropped rather than translated: these are syntax, they never appear as
       * characters a reader sees, and every one of them left in the needle is a
       * guaranteed miss.
       */
      .replace(/^\s*#{1,6}\s+/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*>\s?/gm, '')
      .replace(/[*_`]/g, '')
      // The excerpt's own punctuation: 「…」 means "this was cut" and appears
      // nowhere in the article.
      .replace(/^[\s.…]+/, '')
      .replace(/[\s.…]+$/, '')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Where a quoted passage sits in a body of text.
 *
 * Returns source offsets, or null when the passage is genuinely not there -
 * which is a real answer: an article edited since the check ran no longer
 * contains the sentence the check is about, and pretending otherwise would
 * select the wrong words and then offer to rewrite them.
 *
 * The fallback is deliberate and bounded. When the whole excerpt does not match
 * - the article was edited near one end of it, say - the longest leading run
 * that DOES match is used, down to a floor of eight characters. Below that a
 * match is coincidence: eight Japanese characters is a phrase, three is 「して
 * います」 and would land anywhere.
 */
export function findPassage(
  haystack: string,
  quote: string,
): { from: number; to: number } | null {
  const needle = normaliseQuote(quote);
  if (needle.length < 2) return null;

  const mapped = normalise(haystack);
  const flat = mapped.map((entry) => entry.char).join('');

  const locate = (text: string): { from: number; to: number } | null => {
    const at = flat.indexOf(text);
    if (at === -1) return null;
    const first = mapped[at];
    const last = mapped[at + text.length - 1];
    if (!first || !last) return null;
    // The end is one past the last character's own source position, so a match
    // ending inside a collapsed run still covers the character it matched.
    return { from: first.at, to: last.at + 1 };
  };

  const exact = locate(needle);
  if (exact) return exact;

  /*
   * Trimmed from BOTH ends, longest first.
   *
   * From the end covers an article edited after the quoted range; from the
   * start covers an excerpt that opened with something the document does not
   * contain in that form - a heading the check quoted along with the sentence
   * under it, most often. Only trimming the end was not enough: one bad
   * character at the front defeated every prefix.
   */
  const FLOOR = 8;
  for (let length = needle.length - 1; length >= FLOOR; length -= 1) {
    for (let start = 0; start + length <= needle.length; start += 1) {
      const partial = locate(needle.slice(start, start + length));
      if (partial) return partial;
    }
  }
  return null;
}

/** What ends a Japanese (or English) sentence, and what may trail the mark. */
const SENTENCE_END = /[。！？!?]/;
const TRAILING = /[」』）)】]/;

/**
 * Widen a match to the whole sentence(s) it sits in.
 *
 * A check's quote is an excerpt - twenty characters either side of the hit,
 * cut wherever that lands - so the range findPassage returns usually starts and
 * ends mid-sentence. Rewriting THAT range is what made AIで直す produce text
 * that could not fit: the model was handed 「…め、ピンポイントな…承認手続…」, and
 * its answer replaced a fragment, leaving half a sentence either side of it.
 *
 * Stops at a sentence mark or a line break on each side, so it never reaches
 * into the next paragraph or a heading. A closing bracket after the mark stays
 * with its sentence.
 */
export function sentenceAround(
  text: string,
  range: { from: number; to: number },
): { from: number; to: number } {
  let from = range.from;
  while (from > 0 && text[from - 1] !== '\n' && !SENTENCE_END.test(text[from - 1]!)) from -= 1;
  // A sentence that starts after 「。」 may start after a space too.
  while (from < range.from && /\s/.test(text[from]!)) from += 1;

  let to = Math.max(range.to, from);
  while (to < text.length && text[to] !== '\n' && !SENTENCE_END.test(text[to - 1] ?? '')) to += 1;
  while (to < text.length && TRAILING.test(text[to]!)) to += 1;
  return { from, to };
}
